"use client";

import { useEffect, useState } from "react";
import { X, Heart, Inbox as InboxIcon } from "lucide-react";
import type { Announcement } from "@/lib/types";

interface InboxPanelProps {
  open: boolean;
  onClose: () => void;
  announcements: Announcement[];
}

export default function InboxPanel({
  open,
  onClose,
  announcements,
}: InboxPanelProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [likedIds, setLikedIds] = useState<string[]>([]);
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (open) {
      // Mount first, then animate in on the next tick
      const mountTimer = setTimeout(() => setVisible(true), 10);
      const animTimer = setTimeout(() => {
        setAnimating(true);
        // Load liked state
        const liked = JSON.parse(
          localStorage.getItem("bison_liked_messages") || "[]"
        );
        setLikedIds(liked);
        setLikeCounts(
          Object.fromEntries(announcements.map((a) => [a.id, a.likes]))
        );
      }, 30);
      return () => {
        clearTimeout(mountTimer);
        clearTimeout(animTimer);
      };
    } else {
      // Animate out first, then unmount after transition completes
      const outTimer = setTimeout(() => setAnimating(false), 10);
      const hideTimer = setTimeout(() => setVisible(false), 330);
      return () => {
        clearTimeout(outTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [open, announcements]);

  const toggleLike = async (msgId: string) => {
    const isLiked = likedIds.includes(msgId);

    // Optimistic update
    setLikedIds((prev) =>
      isLiked ? prev.filter((id) => id !== msgId) : [...prev, msgId]
    );
    setLikeCounts((prev) => ({
      ...prev,
      [msgId]: Math.max(0, (prev[msgId] ?? 0) + (isLiked ? -1 : 1)),
    }));

    // Persist like state locally
    const updated = isLiked
      ? likedIds.filter((id) => id !== msgId)
      : [...likedIds, msgId];
    localStorage.setItem("bison_liked_messages", JSON.stringify(updated));

    try {
      await fetch(`/api/announcements/${msgId}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ liked: !isLiked }),
      });
    } catch (e) {
      console.error("Error liking message: ", e);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-300 flex justify-end ${
        animating ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Study Bison Updates"
    >
      <div
        className={`w-full sm:w-[400px] h-full bg-[#0a0a0c] border-l border-zinc-800/80 shadow-2xl flex flex-col transform transition-transform duration-300 ${
          animating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full theme-accent-border-soft border bg-zinc-950 p-0.5 overflow-hidden flex items-center justify-center">
              { }
              <img
                src="/logo.png"
                alt="Study Bison Official Logo"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <h2 className="text-white font-serif-premium font-bold text-lg">
              Study Bison Updates
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-2 hover:bg-zinc-800 rounded-full transition-colors"
            aria-label="Close Inbox"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
          {announcements.length === 0 ? (
            <div className="text-center py-10 text-zinc-500">
              <InboxIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No new messages from Study Bison Admin</p>
            </div>
          ) : (
            announcements.map((msg) => {
              const isLiked = likedIds.includes(msg.id);
              const likes = likeCounts[msg.id] ?? msg.likes;
              return (
                <div
                  key={msg.id}
                  className="bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-4 hover:bg-zinc-800/80 transition-colors flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full theme-accent-border-soft border overflow-hidden shrink-0 bg-zinc-950">
                      { }
                      <img
                        src="/logo.png"
                        alt="Study Bison Admin Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="theme-accent-strong-text font-semibold text-xs tracking-wider uppercase">
                      Study Bison Admin
                    </span>
                    <span className="text-zinc-600 text-[10px] ml-auto">
                      {formatDate(msg.createdAt)}
                    </span>
                  </div>
                  {msg.title && (
                    <h3 className="text-white font-medium text-sm mb-1">
                      {msg.title}
                    </h3>
                  )}
                  <p className="text-zinc-400 text-[13px] leading-[1.6] whitespace-pre-line">
                    {msg.body}
                  </p>
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t border-zinc-800/60">
                    <button
                      onClick={() => toggleLike(msg.id)}
                      className={`flex items-center gap-1.5 text-xs font-medium transition-all active:scale-90 ${
                        isLiked
                          ? "text-red-400 hover:text-red-300"
                          : "text-zinc-500 hover:text-zinc-300"
                      }`}
                      aria-label={isLiked ? "Unlike message" : "Like message"}
                    >
                      <Heart
                        className={`w-4 h-4 ${isLiked ? "fill-red-400" : ""}`}
                      />
                      {likes > 0 ? likes : "Like"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-900/30 text-center">
          <p className="text-zinc-600 text-[10px] tracking-widest uppercase font-medium">
            Powered by Study Bison
          </p>
        </div>
      </div>
    </div>
  );
}
